/**
 * Which registered measures a record header may actually SHOW (#2267 port 6).
 *
 * ── what the legacy header did, and the two things wrong with it ────────────
 * `HeaderMetrics` in `crm/RecordPage.tsx` read `core.measure` rows for the
 * entity and then aggregated them IN THE BROWSER: it pulled a `PAGE_SIZE` page
 * of `crm_opportunity`, one of `core_organization` and every `core.relationship`
 * row, and reduced them with the 0026 filter grammar with `$record` bound.
 *
 *   · **the numbers were a window.** Past 500 rows a tile reported the first
 *     500, which is the exact defect `core.dashboard_measures` was written to
 *     remove (TEC-05 / UX-04, core 0117) and which `MeasureStrip`'s header
 *     calls "plausible, precise, and silent". The legacy page knew — it printed
 *     a `~` — but a tilde is not a number you can quote either.
 *   · **it read three whole tables to draw four small tiles**, on every open of
 *     every party record.
 *
 * ── and the capability underneath it does not exist on the server ───────────
 * MEASURED on this tree. `core.compute_measure(p_key text)` takes a KEY and
 * nothing else; its filter is appended to the aggregate as raw SQL with no
 * bind, and `core.dashboard_measures(p_keys text[])` is the batch form of the
 * same signature. There is no record parameter anywhere in the chain. So a
 * measure scoped to "this record" has no server-side source at all.
 *
 * That is not new: `packages/sdk/src/filters.ts` already refuses to translate a
 * `$`-bind because it is "resolved from context the server does not have", and
 * the console fixture's own `RECORD_MEASURES` header reports the missing
 * record-page scope on **#1466** in as many words. Nothing shipped registers
 * one either — a census of `register_measure` calls across `modules/` and
 * `packages/core/migrations` finds `$record` in ZERO of them, and the only
 * measures registered against a party entity are `core.org_count` and
 * `core.people_count`, which are global counts.
 *
 * ── so this file declines, and says which ones it declined ──────────────────
 * A record-bound measure is not asked for. Sending its key to
 * `dashboard_measures` would return a tile whose filter had been evaluated
 * WITHOUT the record — a number about the whole deployment wearing a label
 * about this record, which is worse than the windowed figure it replaced.
 *
 * The alternative — keeping the client-side reduction for those and using the
 * server for the rest — is two sources for one kind of number, which is how the
 * desks came to disagree with the Overview in the first place.
 */
import type { Row } from '@solum/sdk'

/**
 * One tile the header may draw: the key it asks the server for, and the words
 * it shows.
 *
 * The LABEL travels with the key because the key is an address, not a name
 * (#2406). The strip used to receive `{ key }` alone and fall back to the key
 * whenever the door did not answer, so an unanswered measure put
 * `CRM.OPP.AMOUNT` on a person's record as if it were a caption. The registry
 * row this plan is built from already carries the governed label
 * (`core.measure.label`, `not null` in 0019); dropping it here was the defect.
 */
export interface RecordMeasureTile {
  key: string
  label: string
}

export interface RecordMeasurePlan {
  /** Registered measures this deployment's server can compute, with their registry labels. */
  computable: RecordMeasureTile[]
  /** Registered keys withheld because their filter names a `$`-bind. */
  recordBound: string[]
}

/**
 * Does this measure's filter reference a bind the server cannot resolve?
 *
 * Live `core.measure.filter` is TEXT (a stored SQL boolean expression); the
 * console fixture carries the 0026 leaf ARRAY. Both are scanned in their
 * serialised form rather than parsed, because the question is not what the
 * filter means — it is whether it mentions a value only the client holds, and a
 * substring answers that for either shape. A parser for two grammars would be
 * two chances to be wrong about a security-adjacent "can the server answer
 * this".
 */
export function namesARecordBind(filter: unknown): boolean {
  if (filter == null) return false
  const text = typeof filter === 'string' ? filter : JSON.stringify(filter)
  return /\$record\b/.test(text)
}

const orderOf = (measure: Row): number =>
  measure.sort == null ? Number.MAX_SAFE_INTEGER : Number(measure.sort)

/**
 * The plan for one entity's header: what to ask the server for, and what was
 * withheld.
 *
 * Ordered by the registry's `sort` where it has one — live `core.measure` (0019)
 * has no such column, so the fallback is the key, which is stable and is not a
 * judgement about importance.
 *
 * A row with no label is not drawn. Live cannot produce one (`label text not
 * null`), so the case is a malformed registry, and the only thing the header
 * could print in its place is the key — the exact caption this plan exists to
 * keep off the record.
 */
export function recordMeasurePlan(measures: Row[]): RecordMeasurePlan {
  const ordered = measures.slice().sort((a, b) =>
    orderOf(a) - orderOf(b) || String(a.key).localeCompare(String(b.key)))
  const computable: RecordMeasureTile[] = []
  const recordBound: string[] = []
  for (const measure of ordered) {
    const key = String(measure.key ?? '')
    if (key === '') continue
    if (namesARecordBind(measure.filter)) { recordBound.push(key); continue }
    const label = String(measure.label ?? '').trim()
    if (label === '') continue
    computable.push({ key, label })
  }
  return { computable, recordBound }
}
